import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import AuthService from "services/auth.service";
import { User } from "types/user-schema";

export type AuthStatus = "loading" | "authenticated" | "anonymous";

interface AuthContextValue {
    authStatus: AuthStatus;
    currentUser: User | null;
    setCurrentUser: (user: User | null) => void;
}

export const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [currentUser, setCurrentUserState] = useState<User | null>(null);
    const [authStatus, setAuthStatus] = useState<AuthStatus>("loading");
    const navigate = useNavigate();
    const location = useLocation();

    const setCurrentUser = useCallback((user: User | null) => {
        setCurrentUserState(user);
        setAuthStatus(user ? "authenticated" : "anonymous");
    }, []);

    useEffect(() => {
        if (!AuthService.isAuthenticated()) {
            // Regex rule to determine this /change-password/91651b3ec36b13c159b14e0a7cd03f41ef7b4df7 or /activate/049964947b6e614b41da251f76ef68f04e53fc83
            if (location.pathname.match(/\/(change-password|activate)\/[a-z0-9]{40}/) !== null) {
                const matches = location.pathname.match(/\/(change-password|activate)\/([a-z0-9]{40})/);
                const token = matches?.[2];
                const targetRoute = matches?.[1] === "activate" ? "/activate" : "/change-password";

                if (!token) {
                    setCurrentUser(null);
                    return undefined;
                }

                // save toke in reset-auth
                AuthService.setResetToken(token);
                setCurrentUser(null);
                navigate(targetRoute, { replace: true });
            }
        }
    }, [location.pathname, navigate, setCurrentUser]);

    useEffect(() => {
        let isMounted = true;

        const fetchData = async () => {
            setAuthStatus("loading");

            try {
                const user = await AuthService.getUser();
                if (!isMounted) return;

                setCurrentUser(user);
            } catch (error) {
                if (!isMounted) return;

                setCurrentUser(null);
            }
        };

        if (!AuthService.isAuthenticated()) {
            setCurrentUser(null);
            return () => {
                isMounted = false;
            };
        }

        fetchData();

        return () => {
            isMounted = false;
        };
    }, [setCurrentUser]);

    return (
        <AuthContext.Provider value={{ authStatus, currentUser, setCurrentUser }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);

    if (!context) {
        throw new Error("useAuth must be used within an AuthProvider");
    }

    return context;
};
